package com.vamvisions.main.components.markdown

import com.vamvisions.main.theme.Tokens
import com.vamvisions.main.theme.cssVar
import org.jetbrains.compose.web.css.StyleScope
import org.jetbrains.compose.web.css.StyleSheet

/** Typography of a rendered document, scoped to [article] so it never leaks into the page chrome. */
object MarkdownStyles : StyleSheet() {

    /** A readable measure. Long emails and URLs wrap, so they never widen the page on phones. */
    val article by style {
        property("max-width", "72ch")
        property("overflow-wrap", "anywhere")

        desc(self, "h1") style {
            property("margin", "0 0 ${cssVar(Tokens.SPACE_4)}")
            property("font-size", "clamp(1.8rem, 6vw, 2.6rem)")
            property("font-weight", "400")
            property("line-height", "1.2")
        }
        desc(self, "h2") style {
            property("margin", "${cssVar(Tokens.SPACE_4)} 0 ${cssVar(Tokens.SPACE_3)}")
            property("font-size", "clamp(1.5rem, 5vw, 2rem)")
            property("font-weight", "300")
            property("line-height", "1.25")
        }
        desc(self, "h3") style {
            property("margin", "${cssVar(Tokens.SPACE_4)} 0 ${cssVar(Tokens.SPACE_2)}")
            property("font-size", "1.4rem")
            property("font-weight", "300")
            property("line-height", "1.3")
        }
        desc(self, "h4") style {
            property("margin", "${cssVar(Tokens.SPACE_3)} 0 ${cssVar(Tokens.SPACE_2)}")
            property("font-size", "1.1rem")
            property("font-weight", "700")
        }
        desc(self, "p") style {
            property("margin", "0 0 ${cssVar(Tokens.SPACE_3)}")
        }
        desc(self, "ul") style {
            property("margin", "0 0 ${cssVar(Tokens.SPACE_3)}")
            property("padding-inline-start", "1.5rem")
        }
        desc(self, "ol") style {
            property("margin", "0 0 ${cssVar(Tokens.SPACE_3)}")
            property("padding-inline-start", "1.5rem")
        }
        desc(self, "li") style {
            property("margin-bottom", cssVar(Tokens.SPACE_1))
        }
        // Paragraphs inside list items keep the list compact.
        desc(desc(self, "li"), "p") style {
            property("margin", "0")
        }
        desc(self, "code") style {
            property("padding", "0 ${cssVar(Tokens.SPACE_1)}")
            property("font-size", "0.9em")
            property("font-weight", "700")
            property("background-color", cssVar(Tokens.COLOR_CHIP))
            property("border-radius", "0.25rem")
        }
        desc(self, "hr") style {
            property("margin", "${cssVar(Tokens.SPACE_4)} 0")
            property("border", "none")
            property("border-top", "1px solid ${cssVar(Tokens.COLOR_CHIP)}")
        }
        desc(self, "img") style {
            property("display", "block")
            property("max-width", "100%")
            property("height", "auto")
            property("margin", "0 0 ${cssVar(Tokens.SPACE_3)}")
            property("border-radius", cssVar(Tokens.RADIUS))
        }
    }

    /** Scrolls a wide table sideways inside itself, so the page never scrolls horizontally. */
    val tableWrapper by style {
        property("max-width", "100%")
        property("overflow-x", "auto")
        property("margin", "0 0 ${cssVar(Tokens.SPACE_3)}")
    }

    val table by style {
        property("border-collapse", "collapse")
        property("overflow-wrap", "normal")

        desc(self, "th") style {
            cellBox()
            property("text-align", "start")
            property("background-color", cssVar(Tokens.COLOR_SURFACE))
        }
        desc(self, "td") style {
            cellBox()
        }
    }

    private fun StyleScope.cellBox() {
        property("padding", "${cssVar(Tokens.SPACE_2)} ${cssVar(Tokens.SPACE_3)}")
        property("border", "1px solid ${cssVar(Tokens.COLOR_CHIP)}")
        property("vertical-align", "top")
    }
}
